/**
 * Support Redux Slice
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  SupportTicket,
  SupportTicketStats,
  SupportTicketsFilter,
  PaginatedSupportTickets,
  PaginationMeta,
  CreateSupportTicketRequest,
  UpdateSupportTicketRequest,
} from '../domain/types'
import { DEFAULT_PAGE_SIZE, DEFAULT_SUPPORT_SORT } from '../domain/constants'

export interface CreateTicketPayload extends CreateSupportTicketRequest {
  attachmentFiles?: File[]
}

export interface SupportState {
  items: SupportTicket[]
  selectedTicket: SupportTicket | null
  stats: SupportTicketStats
  filter: SupportTicketsFilter
  pagination: PaginationMeta
  loading: boolean
  error: string | null
}

const initialState: SupportState = {
  items: [],
  selectedTicket: null,
  stats: { open: 0, inProgress: 0, resolved: 0, total: 0 },
  filter: {
    status: [],
    sortBy: DEFAULT_SUPPORT_SORT.sortBy,
    sortOrder: DEFAULT_SUPPORT_SORT.sortOrder,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  },
  pagination: {
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    pageCount: 1,
    total: 0,
  },
  loading: false,
  error: null,
}

export const supportSlice = createSlice({
  name: 'support',
  initialState,
  reducers: {
    setItems: (state, action: PayloadAction<SupportTicket[]>) => {
      state.items = action.payload
      state.loading = false
      state.error = null
    },
    setSelectedTicket: (state, action: PayloadAction<SupportTicket | null>) => {
      state.selectedTicket = action.payload
    },
    setStats: (state, action: PayloadAction<SupportTicketStats>) => {
      state.stats = action.payload
    },
    setFilter: (state, action: PayloadAction<SupportTicketsFilter>) => {
      state.filter = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.loading = false
    },

    // Fetch tickets
    fetchTickets: (state, _action: PayloadAction<SupportTicketsFilter | undefined>) => {
      state.loading = true
      state.error = null
    },
    fetchTicketsSuccess: (state, action: PayloadAction<PaginatedSupportTickets>) => {
      state.items = action.payload.data
      state.pagination = action.payload.pagination
      state.loading = false
    },
    fetchTicketsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Create ticket
    createTicket: (state, _action: PayloadAction<CreateTicketPayload>) => {
      state.loading = true
      state.error = null
    },
    createTicketSuccess: (state, action: PayloadAction<SupportTicket>) => {
      state.items.unshift(action.payload)
      state.stats.open += 1
      state.stats.total += 1
      state.loading = false
    },
    createTicketFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Update ticket
    updateTicket: (state, _action: PayloadAction<{ id: string } & UpdateSupportTicketRequest>) => {
      state.loading = true
      state.error = null
    },
    updateTicketSuccess: (state, action: PayloadAction<SupportTicket>) => {
      const index = state.items.findIndex((t) => t.documentId === action.payload.documentId)
      if (index !== -1) {
        state.items[index] = action.payload
      }
      if (state.selectedTicket?.documentId === action.payload.documentId) {
        state.selectedTicket = action.payload
      }
      state.loading = false
    },
    updateTicketFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },

    // Pagination
    setPage: (state, action: PayloadAction<number>) => {
      state.filter.page = action.payload
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.filter.pageSize = action.payload
      state.filter.page = 1
    },

    // Search/filter
    searchTickets: (state, action: PayloadAction<string>) => {
      const search = action.payload.trim()
      state.filter = { ...state.filter, search: search || undefined, page: 1 }
    },
    filterByStatus: (state, action: PayloadAction<string[]>) => {
      state.filter = { ...state.filter, status: action.payload as any[], page: 1 }
    },
    filterByChannel: (state, action: PayloadAction<string | undefined>) => {
      state.filter = { ...state.filter, channel: action.payload as any, page: 1 }
    },

    clearError: (state) => {
      state.error = null
    },
  },
})

export const supportActions = supportSlice.actions
export const supportReducer = supportSlice.reducer
